<template>
  <div class="pt-4" ref="mainContainer">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mx-4"
    ></v-progress-linear>

    <v-layout row v-if="!loading" :class="{ 'fullscreen-map': isFullscreen }">
      <!-- LEFT PANEL -->
      <v-layout column xs4 style="min-width: 30%" v-show="!isFullscreen">
        <!-- FILTER CARD -->
        <v-card class="ml-3 mb-3 pa-3" elevation="2">
          <v-card-title class="headline pa-0">Filters</v-card-title>
          <v-layout column>
            <v-layout row>
              <v-flex xs6 class="pr-1">
                <v-select
                  multiple
                  v-model="filters.building"
                  :items="buildingOptions"
                  label="Building"
                  dense
                  outlined
                  clearable
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>
                      <span>{{ item }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="grey--text caption"
                      style="margin-bottom:0;padding-bottom:0;"
                      >(+{{ filters.building.length - 1 }} others)</span
                    >
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs6 class="px-1">
                <v-select
                  multiple
                  v-model="filters.branch"
                  :items="branchOptions"
                  label="Branch"
                  dense
                  outlined
                  clearable
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>
                      <span>{{ item }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="grey--text caption"
                      style="margin-bottom:0;padding-bottom:0;"
                      >(+{{ filters.branch.length - 1 }} others)</span
                    >
                  </template>
                </v-select>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6 class="pr-1">
                <v-select
                  multiple
                  v-model="filters.class"
                  :items="classOptions"
                  label="Grade"
                  dense
                  outlined
                  clearable
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>
                      <span>{{ item }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="grey--text caption"
                      style="margin-bottom:0;padding-bottom:0;"
                      >(+{{ filters.class.length - 1 }} others)</span
                    >
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs6 class="pl-1">
                <v-select
                  multiple
                  v-model="filters.section"
                  :items="sectionOptions"
                  label="Section"
                  dense
                  outlined
                  clearable
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>
                      <span>{{ item }}</span>
                    </v-chip>
                    <span
                      v-if="index === 1"
                      class="grey--text caption"
                      style="margin-bottom:0;padding-bottom:0;"
                      >(+{{ filters.section.length - 1 }} others)</span
                    >
                  </template>
                </v-select>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card>

        <!-- STUDENT TABLE -->
        <v-card class="ml-3 mb-3" elevation="2">
          <v-card-title class="headline">Students</v-card-title>
          <v-data-table
            :headers="studentHeaders"
            :items="filteredStudents"
            :pagination.sync="studentPagination"
            item-key="uniqueId"
            class="elevation-0 pl-3"
            :rows-per-page-items="[10, 25, 50]"
            @click:row="selectStudentLocation"
          >
            <template v-slot:items="props">
              <tr
                @click="selectStudentLocation(props.item)"
                style="cursor:pointer;"
              >
                <td style="padding:6px !important;">
                  <strong>{{ props.item.fullName }}</strong>
                  <div class="text--secondary" style="font-size:0.8rem;">
                    {{ props.item.busAddress1 }}
                  </div>
                </td>
                <td style="padding:6px !important;">
                  {{ props.item.class }}
                </td>
                <td style="padding:6px !important;">
                  {{ props.item.building }}
                </td>
                <td style="padding:6px !important;">
                  {{ props.item.section }}
                </td>
                <td style="padding:6px !important;">
                  {{ props.item.branch }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>

        <!-- SELECTED MARKERS -->
        <v-card class="mx-3" elevation="2">
          <v-card-title class="headline d-flex justify-space-between">
            <div>Selected Markers ({{ selected.length }})</div>
            <v-btn small color="success" outline @click="clearSelection">
              Clear All
            </v-btn>
          </v-card-title>

          <div
            class="selected-list pa-2"
            style="max-height:400px;overflow-y:auto;"
          >
            <div
              v-for="loc in selected"
              :key="loc.id"
              class="px-2 mb-2 border rounded"
              style="background:#f9f9f9;"
            >
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <strong>{{ loc.name }}</strong>
                  <span class="ml-2 grey--text">
                    ({{ loc.residents.length }} students)
                  </span>
                </div>
                <div class="ml-auto d-flex">
                  <v-btn
                    @click.stop="toggleExpanded(loc)"
                    flat
                    icon
                    color="success"
                    class="mr-1"
                  >
                    <v-icon>
                      {{ loc.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    @click.stop="removeLocation(loc)"
                    flat
                    icon
                    color="error"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
              <div v-if="loc.expanded" class="mt-2 pl-3">
                <div
                  v-for="res in loc.residents"
                  :key="res.uniqueId"
                  class="py-1 text--secondary"
                  style="font-size:0.85rem;"
                >
                  {{ res.fullName }} ({{ res.class }}-{{ res.section }})
                  <span class="grey--text text--lighten-1">
                    • {{ res.building }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <v-card-actions class="ml-2 mb-1">
            <v-btn
              color="success"
              small
              @click="openDialog"
              :disabled="!selected.length"
            >
              View All Selected Students
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-layout>

      <!-- RIGHT PANEL: MAP -->
      <v-layout
        column
        :class="isFullscreen ? 'xs12' : 'xs8'"
        style="min-width:70%;position:relative;"
      >
        <div
          class="instructions mx-3 pa-3 mb-2"
          style="background:#f8f9fa;border-radius:4px;font-size:14px;"
          v-show="!isFullscreen"
        >
          <strong>Select markers by:</strong>
          <ul class="ml-4 mt-1">
            <li>Clicking a marker</li>
            <li>Clicking a student row</li>
            <li>Shift + drag to draw rectangle</li>
          </ul>
        </div>

        <div class="map-container pa-3" :style="mapContainerStyle">
          <l-map
            ref="map"
            :zoom="zoom"
            :center="center"
            @ready="onMapReady"
            class="geo-map"
          >
            <l-tile-layer
              :url="tileUrl"
              :attribution="attribution"
            ></l-tile-layer>

            <!-- MARKERS – even smaller -->
            <l-marker
              v-for="loc in locations"
              :key="loc.id"
              :lat-lng="[loc.lat, loc.lng]"
              :icon="
                loc.id === lastClickedId ? selectedMarkerIcon : tinyDefaultIcon
              "
              @click="toggleSelect(loc)"
            >
              <l-popup>
                <strong>{{ loc.name }}</strong
                ><br />
                Students: {{ loc.residents.length }}<br />
                <ul class="mt-2 pl-3" style="font-size:0.9rem;">
                  <li v-for="r in loc.residents" :key="r.uniqueId">
                    {{ r.fullName }} ({{ r.class }}-{{ r.section }})
                  </li>
                </ul>
              </l-popup>
            </l-marker>
          </l-map>
        </div>
      </v-layout>
    </v-layout>

    <!-- DIALOG -->
    <v-dialog v-model="dialog" max-width="600px" :style="{ zIndex: 10000 }">
      <v-card>
        <v-card-title class="headline success white--text">
          Selected Students ({{ selectedStudentsList.length }})
        </v-card-title>
        <v-card-text class="pa-0">
          <div v-if="selectedStudentsList.length" class="scrollable-list">
            <div
              v-for="s in selectedStudentsList"
              :key="s.uniqueId"
              class="pa-3 border-bottom"
            >
              <div class="font-weight-bold">{{ s.fullName }}</div>
              <div class="text--secondary" style="font-size:0.85rem;">
                {{ s.class }} • {{ s.section }} • {{ s.building }} •
                {{ s.busAddress1 }}
              </div>
            </div>
          </div>
          <div v-else class="pa-6 text-center grey--text">
            No students selected
          </div>
        </v-card-text>
        <v-card-actions class="mr-0">
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="exportToCSV"
            :disabled="!selectedStudentsList.length"
            class=""
          >
            Export CSV
          </v-btn>
          <v-btn
            color="default"
            dark
            @click="dialog = false"
            class="mr-3 ml-2 my-2"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default {
  name: "MapSelector",
  components: { LMap, LTileLayer, LMarker, LPopup },
  data() {
    // DEFAULT: 12px blue circle
    const createCircleIcon = (size, color) => {
      const radius = size / 2;
      const strokeWidth = 1.5;
      const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <circle
          cx="${radius}"
          cy="${radius}"
          r="${radius - strokeWidth}"
          fill="${color}"
          stroke="#fff"
          stroke-width="${strokeWidth}"
        />
      </svg>
    `;
      return L.divIcon({
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        className: "custom-circle-marker",
      });
    };

    // const selectedMarkerIcon = L.icon({
    //     iconUrl:
    //         "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
    //     iconRetinaUrl:
    //         "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    //     shadowUrl:
    //         "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    //     iconSize: [16, 26],
    //     iconAnchor: [8, 26],
    //     popupAnchor: [0, -22],
    //     shadowSize: [26, 26],
    // });

    const selectedMarkerIcon = L.icon({
      iconUrl:
        "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
      iconRetinaUrl:
        "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
      shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    return {
      zoom: 12,
      center: [40.7128, -73.95],
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "&copy; OpenStreetMap contributors",
      selected: [],
      lastClickedId: null,
      map: null,
      rectangle: null,
      startLatLng: null,
      addressInfos: [],
      students: [],
      loading: true,
      studentPagination: { rowsPerPage: 10 },
      filters: { building: [], branch: [], class: [], section: [] },
      studentHeaders: [
        { text: "Name", value: "fullName" },
        { text: "Grade", value: "class" },
        { text: "Bldg", value: "building" },
        { text: "Sec", value: "section" },
        { text: "Branch", value: "branch" },
      ],
      classOptions: [],
      buildingOptions: [],
      branchOptions: [],
      sectionOptions: [],
      dialog: false,
      tinyDefaultIcon: createCircleIcon(12, "#d32f2f"),
      selectedMarkerIcon,
      isFullscreen: false,
      printingPDF: false,
    };
  },
  computed: {
    filteredStudents() {
      return this.students.filter((s) => {
        return (
          (this.filters.building.length === 0 ||
            this.filters.building.includes(s.building)) &&
          (this.filters.branch.length === 0 ||
            this.filters.branch.includes(s.branch)) &&
          (this.filters.class.length === 0 ||
            this.filters.class.includes(s.class)) &&
          (this.filters.section.length === 0 ||
            this.filters.section.includes(s.section))
        );
      });
    },
    locations() {
      const grouped = this.filteredStudents.reduce((acc, s) => {
        const key = `${s.busHouse}|${s.busStreet}`;
        if (!acc[key]) {
          acc[key] = {
            house: s.busHouse,
            street: s.busStreet,
            residents: [],
            expanded: false,
            building: s.building,
          };
        }
        acc[key].residents.push(s);
        return acc;
      }, {});

      return Object.values(grouped)
        .map((g) => {
          const info = this.addressInfos.find(
            (a) => a.house === g.house && a.street === g.street
          );
          if (
            !info ||
            !["11211", "11249", "11205", "11206"].includes(info.zipcode)
          )
            return null;
          return {
            id: info.id,
            name: `${g.house} ${g.street}`,
            lat: parseFloat(info.busAddressLatitude),
            lng: parseFloat(info.busAddressLongitude),
            residents: g.residents,
            expanded: g.expanded,
            building: g.building,
          };
        })
        .filter(Boolean);
    },
    selectedStudentsList() {
      return this.selected.flatMap((loc) => loc.residents || []);
    },
    mapContainerStyle() {
      return this.isFullscreen
        ? { height: "calc(100vh - 100px)", padding: "0" }
        : {};
    },
  },
  watch: {
    filters: {
      handler() {
        this.$nextTick(() => {
          if (this.map && this.locations.length) {
            const bounds = L.latLngBounds(
              this.locations.map((l) => [l.lat, l.lng])
            );
            this.map.fitBounds(bounds, { padding: [50, 50] });
          }
        });
      },
      deep: true,
    },
  },
  created() {
    this.loadStudents();
  },
  methods: {
    async loadStudents() {
      try {
        this.loading = true;
        const res = await this.$http.get("api/geoStudents/getStudents");
        this.students = res.map((s, i) => ({
          ...s,
          fullName:
            `${s.hebName || ""} ${s.lastNameH || ""}`.trim() || s.engName,
          uniqueId: `${(s.studentID || "").trim() || "unknown"}-${i}`,
        }));
        await this.loadAddressInfos();

        const naturalSort = (a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: "base",
          });

        this.classOptions = [
          ...new Set(this.students.map((s) => s.class).filter(Boolean)),
        ].sort(naturalSort);
        this.buildingOptions = [
          ...new Set(this.students.map((s) => s.building).filter(Boolean)),
        ].sort(naturalSort);
        this.branchOptions = [
          ...new Set(this.students.map((s) => s.branch).filter(Boolean)),
        ].sort(naturalSort);
        this.sectionOptions = [
          ...new Set(this.students.map((s) => s.section).filter(Boolean)),
        ].sort(naturalSort);

        this.loading = false;
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    async loadAddressInfos() {
      try {
        const data = await this.$http.get("api/geoStudents/getAddressInfos");
        this.addressInfos = data;
      } catch (e) {
        console.error(e);
      }
    },
    toggleSelect(loc) {
      const i = this.selected.findIndex((x) => x.id === loc.id);
      if (i > -1) {
        this.selected.splice(i, 1);
        if (this.lastClickedId === loc.id) this.lastClickedId = null;
      } else {
        loc.expanded = false;
        this.selected.push(loc);
      }
    },
    toggleExpanded(loc) {
      loc.expanded = !loc.expanded;
      this.$forceUpdate();
    },
    removeLocation(loc) {
      const i = this.selected.findIndex((x) => x.id === loc.id);
      if (i > -1) {
        this.selected.splice(i, 1);
        if (this.lastClickedId === loc.id) this.lastClickedId = null;
      }
    },
    selectStudentLocation(student) {
      const loc = this.locations.find((l) =>
        l.residents.some(
          (r) =>
            r.busHouse === student.busHouse && r.busStreet === student.busStreet
        )
      );
      if (loc && !this.selected.some((x) => x.id === loc.id)) {
        loc.expanded = false;
        this.selected.push(loc);
      }
      if (loc) {
        this.lastClickedId = loc.id;
        if (this.map) this.map.setView([loc.lat, loc.lng], 15);
      }
    },
    clearSelection() {
      this.selected = [];
      this.lastClickedId = null;
    },
    openDialog() {
      this.dialog = true;
    },

    // ------------------- MAP READY + NEW CONTROLS -------------------
    onMapReady(map) {
      this.map = map;
      if (this.locations.length) {
        const bounds = L.latLngBounds(
          this.locations.map((l) => [l.lat, l.lng])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
      this.setupAreaSelection();
      this.addClearControl();
      this.addRightSideControls();
    },

    // ------------------- RIGHT-SIDE BUTTONS (PDF + Fullscreen) -------------------
    addRightSideControls() {
      const RightControl = L.Control.extend({
        options: { position: "topright" },
        onAdd: () => {
          const container = L.DomUtil.create("div", "leaflet-bar");
          container.style.background = "transparent";
          container.style.boxShadow = "none";
          container.style.display = "flex";
          container.style.flexDirection = "column";
          container.style.gap = "1px";

          // PDF Button with loading state
          const pdfBtn = L.DomUtil.create("a", "", container);
          pdfBtn.href = "#";
          pdfBtn.title = "Export / Print as PDF";
          pdfBtn.style.cssText = `
        display: block; width: 30px; height: 30px;
        background: white; text-align: center;
        border: 2px solid #ccc; border-radius: 3px;
        cursor: pointer; line-height: 26px;
        position: relative;
      `;

          // Icon container
          const iconSpan = L.DomUtil.create("i", "mdi", pdfBtn);
          iconSpan.style.fontSize = "18px";
          iconSpan.style.lineHeight = "26px";
          iconSpan.style.color = "#555";

          const updatePdfIcon = () => {
            if (this.printingPDF) {
              iconSpan.className = "mdi mdi-loading mdi-spin";
              iconSpan.style.color = "#1976d2";
              pdfBtn.style.cursor = "wait";
            } else {
              iconSpan.className = "mdi mdi-file-pdf-box";
              iconSpan.style.color = "#555";
              pdfBtn.style.cursor = "pointer";
            }
          };
          updatePdfIcon();

          L.DomEvent.disableClickPropagation(pdfBtn);
          pdfBtn.onclick = (e) => {
            e.preventDefault();
            if (!this.printingPDF) {
              this.printMap().then(() => updatePdfIcon());
            }
          };

          // Fullscreen Button (unchanged)
          const fsBtn = L.DomUtil.create("a", "", container);
          fsBtn.href = "#";
          fsBtn.title = "Toggle Fullscreen";
          const updateFsIcon = () => {
            fsBtn.innerHTML = `<i class="mdi mdi-${
              this.isFullscreen ? "fullscreen-exit" : "fullscreen"
            }" style="font-size:18px;line-height:26px;color:#555;"></i>`;
          };
          updateFsIcon();
          fsBtn.style.cssText = `
        display: block; width: 30px; height: 30px;
        background: white; text-align: center;
        border: 2px solid #ccc; border-radius: 3px;
        cursor: pointer; line-height: 26px;
      `;
          L.DomEvent.disableClickPropagation(fsBtn);
          fsBtn.onclick = (e) => {
            e.preventDefault();
            this.toggleFullscreen();
            updateFsIcon();
          };

          // Watch printingPDF to update icon
          this.$watch("printingPDF", updatePdfIcon);

          return container;
        },
      });

      this.map.addControl(new RightControl());
    },

    setupAreaSelection() {
      const map = this.map;
      map.on("mousedown", (e) => {
        if (e.originalEvent.shiftKey) {
          if (this.rectangle) map.removeLayer(this.rectangle);
          this.startLatLng = e.latlng;
          this.rectangle = L.rectangle([e.latlng, e.latlng], {
            color: "#3388ff",
            weight: 2,
            fillOpacity: 0.2,
          }).addTo(map);
          map.dragging.disable();
        }
      });
      map.on("mousemove", (e) => {
        if (this.rectangle && this.startLatLng) {
          this.rectangle.setBounds(L.latLngBounds(this.startLatLng, e.latlng));
        }
      });
      map.on("mouseup", () => {
        if (this.rectangle) {
          const b = this.rectangle.getBounds();
          this.locations.forEach((loc) => {
            if (
              b.contains([loc.lat, loc.lng]) &&
              !this.selected.some((s) => s.id === loc.id)
            ) {
              loc.expanded = false;
              this.selected.push(loc);
            }
          });
          this.startLatLng = null;
          map.dragging.enable();
        }
      });
      map.on("click", (e) => {
        if (this.rectangle && !this.rectangle.getBounds().contains(e.latlng)) {
          map.removeLayer(this.rectangle);
          this.rectangle = null;
        }
      });
    },
    addClearControl() {
      const ClearControl = L.Control.extend({
        options: { position: "topleft" },
        onAdd: () => {
          const el = L.DomUtil.create(
            "div",
            "leaflet-bar leaflet-control leaflet-control-custom"
          );
          el.innerHTML =
            '<a title="Clear Selection" style="font-size:18px;line-height:26px;text-align:center;display:block;">X</a>';
          el.style.background = "white";
          el.style.width = "30px";
          el.style.height = "30px";
          el.style.cursor = "pointer";
          el.onclick = () => {
            this.clearSelection();
            if (this.rectangle) {
              this.map.removeLayer(this.rectangle);
              this.rectangle = null;
            }
          };
          return el;
        },
      });
      this.map.addControl(new ClearControl());
    },

    /* ---------- FULLSCREEN ---------- */
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      this.$nextTick(() => {
        if (this.map) this.map.invalidateSize();
      });
    },

    /* ---------- PDF EXPORT (LANDSCAPE) ---------- */
    async printMap() {
      if (this.printingPDF) return;
      this.printingPDF = true;

      try {
        this.map.fire("moveend");
        await this.$nextTick();

        const mapNode = this.$refs.map.$el;
        const canvas = await html2canvas(mapNode, {
          useCORS: true,
          scale: 2,
          logging: false,
          backgroundColor: null,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });

        const pdfW = pdf.internal.pageSize.getWidth();
        const pdfH = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgH = (imgProps.height * pdfW) / imgProps.width;

        // === ADD MAP IMAGE ===
        pdf.addImage(imgData, "PNG", 0, 0, pdfW, imgH);

        // === PREPARE FILTERS & TOTAL STUDENTS ===
        const margin = 15;
        const lineHeight = 5;
        const minSpaceNeeded = 30; // title + 2 lines + padding
        const spaceLeft = pdfH - imgH;
        let y = imgH + 8;

        const activeFilters = [];
        if (this.filters.building.length)
          activeFilters.push(`Building: ${this.filters.building.join(", ")}`);
        if (this.filters.branch.length)
          activeFilters.push(`Branch: ${this.filters.branch.join(", ")}`);
        if (this.filters.class.length)
          activeFilters.push(`Grade: ${this.filters.class.join(", ")}`);
        if (this.filters.section.length)
          activeFilters.push(`Section: ${this.filters.section.join(", ")}`);

        const totalFilteredStudents = this.filteredStudents.length;

        // === ADD INFO ON FIRST PAGE IF SPACE ===
        if (spaceLeft > minSpaceNeeded) {
          pdf.setFontSize(11);
          pdf.setFont("helvetica", "bold");
          pdf.text("Selected Filters", margin, y);
          y += lineHeight + 1;

          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(10);

          if (activeFilters.length === 0) {
            pdf.setFontStyle("italic");
            pdf.text("No filters applied", margin, y);
            y += lineHeight;
          } else {
            activeFilters.forEach((filter) => {
              if (y + lineHeight > pdfH - 15) return;
              pdf.text(filter, margin, y);
              y += lineHeight;
            });
          }

          // Add Total Filtered Students
          y += 2;
          pdf.setFont("helvetica", "bold");
          pdf.text(
            `Total Students (Filtered): ${totalFilteredStudents}`,
            margin,
            y
          );
        } else {
          // === FALLBACK: NEW PAGE ===
          pdf.addPage();
          let y = margin;

          pdf.setFontSize(11);
          pdf.setFont("helvetica", "bold");
          pdf.text("Selected Filters", margin, y);
          y += lineHeight + 1;

          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(10);

          if (activeFilters.length === 0) {
            pdf.setFontStyle("italic");
            pdf.text("No filters applied", margin, y);
            y += lineHeight;
          } else {
            activeFilters.forEach((filter) => {
              pdf.text(filter, margin, y);
              y += lineHeight;
            });
          }

          y += 2;
          pdf.setFont("helvetica", "bold");
          pdf.text(
            `Total Students (Filtered): ${totalFilteredStudents}`,
            margin,
            y
          );
        }

        // === OPEN PDF ===
        const blob = pdf.output("blob");
        const url = URL.createObjectURL(blob);
        const popup = window.open(url, "_blank");

        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          throw new Error("POPUP_BLOCKED");
        }
      } catch (err) {
        console.error(err);

        if (err.message === "POPUP_BLOCKED") {
          this.$notify({
            text:
              "PDF was blocked by your browser. Please allow popups for this site and try again.",
            group: "notification",
            type: "error",
            duration: 8000,
          });
        } else {
          this.$notify({
            text: "Failed to generate PDF. Try again.",
            group: "notification",
            type: "error",
          });
        }
      } finally {
        this.printingPDF = false;
      }
    },
    exportToCSV() {
      // Add UTF-8 BOM for Hebrew support
      const BOM = "\uFEFF";
      const data = this.selectedStudentsList.map((s) => ({
        studentID: s.studentID || "",
        familyID: s.familyID || "",
        fullName: s.fullName || "",
        class: s.class || "",
        section: s.section || "",
        building: s.building || "",
        branch: s.branch || "",
        busAddress1: s.busAddress1 || "",
        busHouse: s.busHouse || "",
        busStreet: s.busStreet || "",
      }));

      // Force studentID as text by prefixing with ="
      const headers = [
        '=T("studentID")',
        '=T("familyID")',
        '=T("fullName")',
        '=T("class")',
        '=T("section")',
        '=T("building")',
        '=T("branch")',
        '=T("busAddress1")',
        '=T("busHouse")',
        '=T("busStreet")',
      ].join(",");

      const rows = data
        .map((row) =>
          [
            `="${row.studentID}"`, // ← Excel: keeps 0025641
            `="${row.familyID}"`,
            `"${row.fullName}"`, // ← UTF-8 safe
            `"${row.class}"`,
            `"${row.section}"`,
            `"${row.building}"`,
            `"${row.branch}"`,
            `"${row.busAddress1}"`,
            `"${row.busHouse}"`,
            `"${row.busStreet}"`,
          ].join(",")
        )
        .join("\n");

      const csv = BOM + headers + "\n" + rows;

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `selected_students_${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.map-container {
  height: 900px;
  width: 100%;
  position: relative;
  transition: all 0.3s ease;
}
.geo-map {
  border: 2px solid #4caf50;
  height: 100%;
  border-radius: 4px;
}
.scrollable-list {
  max-height: 400px;
  overflow-y: auto;
}
.border-bottom {
  border-bottom: 1px solid #eee;
}
.fullscreen-map .map-container {
  height: calc(100vh - 100px) !important;
  padding: 0 !important;
}
.custom-circle-marker {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
</style>
